import {
	createAction,
	createSlice,
	Draft,
	EntityState,
	PayloadAction,
	Update,
} from '@reduxjs/toolkit';
import {
	IClientMessage,
	IClientMessageMeta,
} from '../../interfaces/IClientMessage';
import { messagesAdapter } from '../adapters/messages.adapter';
import { set as _set } from 'lodash';
import {
	IChatMessages,
	isCommandMessageForm,
	isCommandMessageSurvey,
	isEventMessagePostback,
	isEventMessageSurveyData,
} from '@enuan/chat__utils';
import { KeyString } from '@avalenti89/typescript-utils';
import {
	popMessageStack,
	resetReducer,
} from '../../reduxStore/actions/actions';
import { isRenderableMessage } from '../../utils/isRenderableMessage';

const box = createAction('@box/messages/update');

const toggleValue = <
	T extends Record<string, any>,
	K extends KeyString<T> = KeyString<T>,
>(
	object: T,
	key: K,
	value: T[K],
) => {
	const { [key]: _value, ...rest } = object;
	const newValue = value ?? !_value;
	return {
		...rest,
		[key]: newValue,
	};
};

const getReadChanges = (messages: IClientMessage[]) => {
	return messages.reduce<
		{
			id: IClientMessage['client_id'];
			changes: Partial<IClientMessage>;
		}[]
	>((prev, curr) => {
		if (!curr._meta.read && !!curr._meta.sent) {
			return [
				...prev,
				{
					id: curr.client_id,
					changes: {
						_meta: {
							...curr._meta,
							read: true,
						},
					},
				},
			];
		} else {
			return prev;
		}
	}, []);
};

const getCommandChanges = (
	messages: IClientMessage[],
	message?: IClientMessage,
) => {
	if (isEventMessagePostback(message) || isEventMessageSurveyData(message)) {
		const command = messages.find(
			(msg) =>
				((isCommandMessageForm(msg) && !msg._data.parameters.data.reusable) ||
					isCommandMessageSurvey(msg)) &&
				msg.metadata.id === message._data.parameters.metadata.command_id,
		);
		if (command) {
			const form = isEventMessagePostback(message)
				? message._data.parameters.payload
				: message._data.parameters.data;
			return {
				id: command.client_id,
				changes: {
					_meta: {
						...command._meta,
						disabled: true,
						form,
					},
				},
			};
		}
	}
	return undefined;
};

const addReceivedMessage = (
	state: Draft<EntityState<IClientMessage<IChatMessages>>>,
	{ payload: message }: PayloadAction<IClientMessage>,
) => {
	messagesAdapter.upsertOne(state, message);
	const all = messagesAdapter.getSelectors().selectAll(state);
	const command = getCommandChanges(all, message);
	if (command) {
		messagesAdapter.updateOne(state, command);
	}
};

/** Extra fields added on top of the normalized entity state. */
type IMessagesExtraState = {
	/**
	 * Whether the server has confirmed that no older messages exist.
	 * Set to `true` when a history fetch returns an empty array.
	 * Reset to `false` when a trim removes old messages so they can be
	 * re-fetched on the next upward scroll.
	 */
	historyExhausted: boolean;
};

export const messagesSlice = createSlice({
	name: 'messages',
	initialState: messagesAdapter.getInitialState<IMessagesExtraState>({
		historyExhausted: false,
	}),
	reducers: {
		setEchoTimestamp: (
			state,
			action: PayloadAction<{
				id: IClientMessage['client_id'];
				timestamp: IClientMessage['timestamp'];
			}>,
		) => {
			const { id, timestamp } = action.payload;
			messagesAdapter.updateOne(state, {
				id,
				changes: {
					timestamp,
				},
			});
		},
		toggleDebug: (
			state,
			action: PayloadAction<{
				client_id: IClientMessage['client_id'];
				debug?: IClientMessageMeta['debug'];
			}>,
		) => {
			const { client_id, debug } = action.payload;
			const message = messagesAdapter
				.getSelectors()
				.selectById(state, client_id);

			if (message) {
				const { debug: _debug, ...extra } = message._meta ?? {};
				const _meta = {
					...extra,
					debug: debug ?? !_debug,
				};
				messagesAdapter.updateOne(state, {
					id: client_id,
					changes: { _meta },
				});
			}
		},
		toggleRead: (
			state,
			action: PayloadAction<{
				client_id: IClientMessage['client_id'];
				read?: IClientMessageMeta['read'];
			}>,
		) => {
			const { client_id, read } = action.payload;
			const message = messagesAdapter
				.getSelectors()
				.selectById(state, client_id);
			if (message) {
				const { read: _read, ...extra } = message._meta ?? {};
				const newRead = read ?? !_read;
				const _meta = {
					...extra,
					read: newRead,
				};
				messagesAdapter.updateOne(state, {
					id: client_id,
					changes: { _meta },
				});
			}
		},
		toggleSelected: (
			state,
			action: PayloadAction<{
				client_id: IClientMessage['client_id'];
				selected?: IClientMessageMeta['selected'];
			}>,
		) => {
			const { client_id, selected } = action.payload;
			const message = messagesAdapter
				.getSelectors()
				.selectById(state, client_id);
			if (message) {
				const { selected: _selected, ...extra } = message._meta ?? {};
				const newSelected = selected ?? !_selected;
				const _meta = {
					...extra,
					selected: newSelected,
				};
				messagesAdapter.updateOne(state, {
					id: client_id,
					changes: { _meta },
				});
			}
		},
		toggleDisabled: (
			state,
			action: PayloadAction<{
				client_id: IClientMessage['client_id'];
				disabled?: IClientMessageMeta['disabled'];
			}>,
		) => {
			const { client_id, disabled } = action.payload;
			const message = messagesAdapter
				.getSelectors()
				.selectById(state, client_id);
			if (message) {
				const _meta = toggleValue(message._meta ?? {}, 'disabled', disabled);
				messagesAdapter.updateOne(state, {
					id: client_id,
					changes: { _meta },
				});
			}
		},
		toggleError: (
			state,
			action: PayloadAction<{
				client_id: IClientMessage['client_id'];
				error?: IClientMessageMeta['error'];
			}>,
		) => {
			const { client_id, error } = action.payload;
			const message = messagesAdapter
				.getSelectors()
				.selectById(state, client_id);
			if (message) {
				const _meta = toggleValue(message._meta ?? {}, 'error', error);
				messagesAdapter.updateOne(state, {
					id: client_id,
					changes: { _meta },
				});
			}
		},
		setMeta: (
			state,
			{
				payload: { client_id, _meta, override },
			}: PayloadAction<{
				client_id: IClientMessage['client_id'];
				_meta: Partial<IClientMessageMeta>;
				override?: boolean;
			}>,
		) => {
			const { _meta: __meta } =
				messagesAdapter.getSelectors().selectById(state, client_id) ?? {};
			if (__meta) {
				const newMeta = override ? _meta : { ...__meta, ..._meta };
				messagesAdapter.updateOne(state, {
					id: client_id,
					changes: {
						_meta: newMeta,
					},
				});
			}
		},
		setForm: (
			state,
			action: PayloadAction<{
				client_id: IClientMessage['client_id'];
				form: IClientMessageMeta['form'] | undefined;
			}>,
		) => {
			const { client_id, form } = action.payload;

			const message = messagesAdapter
				.getSelectors()
				.selectById(state, client_id);
			if (message) {
				const _meta = {
					...message._meta,
					form,
				};
				messagesAdapter.updateOne(state, {
					id: client_id,
					changes: { _meta },
				});
			}
		},
		addSentMessage: (
			state,
			{ payload: message }: PayloadAction<IClientMessage>,
		) => {
			messagesAdapter.upsertOne(state, message);

			const all = messagesAdapter.getSelectors().selectAll(state);
			const command = getCommandChanges(all, message);
			if (command) {
				messagesAdapter.updateOne(state, command);
			}
			const changes = getReadChanges(all).filter(
				Boolean,
			) as Update<IClientMessage>[];
			if (changes.length) {
				messagesAdapter.updateMany(state, changes);
			}
		},
		addReceivedMessage,
		/**
		 * Removes all messages from the store and resets `historyExhausted` to
		 * `false`, ensuring that history can be reloaded from scratch (e.g. after
		 * a chat restart triggered by {@link InstanceProvider}).
		 */
		clearHistory: (state) => {
			messagesAdapter.removeAll(state);
			state.historyExhausted = false;
		},
		/**
		 * Records whether the server has confirmed there are no more older messages.
		 * When `true`, history fetches are skipped until a trim resets the flag.
		 *
		 * @param payload - `true` to mark history as exhausted; `false` to re-enable fetching.
		 */
		setHistoryExhausted: (state, { payload }: PayloadAction<boolean>) => {
			state.historyExhausted = payload;
		},
		/**
		 * Removes the oldest messages from the store, keeping only the most recent
		 * `keepRenderableCount` renderable messages plus any non-renderable messages
		 * that fall after the new oldest renderable boundary.
		 *
		 * All messages older than the boundary (renderable or not) are removed.
		 * After removal, `historyExhausted` is reset to `false` so the next upward
		 * scroll can re-fetch the removed messages.
		 *
		 * @param keepRenderableCount - Number of renderable messages to keep (from the end).
		 */
		trimOldMessages: (
			state,
			{ payload: keepRenderableCount }: PayloadAction<number>,
		) => {
			const all = messagesAdapter.getSelectors().selectAll(state);
			const renderable = all.filter(isRenderableMessage);

			// Nothing to trim when the store already fits within the target count.
			if (renderable.length <= keepRenderableCount) return;

			// Oldest renderable message that should be kept.
			const boundary = renderable[renderable.length - keepRenderableCount];
			const boundaryIndex = all.findIndex(
				(m) => m.client_id === boundary.client_id,
			);

			// Remove all messages (renderable or not) before the boundary index.
			const idsToRemove = all.slice(0, boundaryIndex).map((m) => m.client_id);
			messagesAdapter.removeMany(state, idsToRemove);

			// Re-enable history fetching so removed messages can be re-loaded.
			state.historyExhausted = false;
		},
		setHistory: (
			state,
			{ payload: messages }: PayloadAction<IClientMessage[]>,
		) => {
			messagesAdapter.upsertMany(state, messages);
			const all = messagesAdapter.getSelectors().selectAll(state);

			messages.forEach((message) => {
				const command = getCommandChanges(all, message);
				if (command) {
					messagesAdapter.updateOne(state, command);
				}
				if (
					(isCommandMessageForm(message) &&
						!message._data.parameters.data.reusable) ||
					isCommandMessageSurvey(message)
				) {
					const event = all
						.toReversed()
						.find(
							(msg) =>
								(isEventMessagePostback(msg) ||
									isEventMessageSurveyData(msg)) &&
								msg.timestamp! > message.timestamp! &&
								msg._data.parameters.metadata.command_id ===
									message.metadata?.id,
						);
					const event_command = getCommandChanges([message], event);
					if (event_command) {
						messagesAdapter.updateOne(state, event_command);
					}
				}
			});
		},
	},
	extraReducers: (builder) => {
		builder
			.addCase(box, (state, action) =>
				_set(
					state,
					(action.payload as unknown as { id: string }).id,
					(action.payload as unknown as { value: any }).value,
				),
			)
			.addCase(resetReducer, () =>
				messagesAdapter.getInitialState<IMessagesExtraState>({
					historyExhausted: false,
				}),
			)
			.addCase(popMessageStack, addReceivedMessage)
			.addDefaultCase(() => {});
	},
});

// export const messagesSlice: typeof _messagesSlice = _messagesSlice;
